import {
  useCompleteOnboarding,
  useRequestCallback,
  useOnboarding,
  useOnboardingClientId,
  useSaveOnboardingDraft,
  useStartAnalysis,
} from "@/features/onboarding/hooks/use-onboarding";
import {
  trackAnalysisOutcome,
  trackCompleted,
  trackError,
  trackStepViewed,
} from "@/features/onboarding/analytics";
import type {
  OnboardingAnalysisStatus,
  OnboardingDraft,
  OnboardingResult,
  OnboardingStep,
} from "@/features/onboarding/types";
import { useTranslation } from "@/features/utils/i18n";
import { formatPhoneNumberIntl } from "react-phone-number-input";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { onboardingErrorMessage } from "@/features/onboarding/errors";
import { Button } from "@/components/ui/button";
import { OnboardingChatProvider, type OnboardingTurn } from "./components/chat";
import { StepAgent } from "./components/step-agent";
import { StepCompany } from "./components/step-company";
import { StepDone } from "./components/step-done";
import { StepHours } from "./components/step-hours";
import { StepMobile } from "./components/step-mobile";
import { StepTelephony } from "./components/step-telephony";
import { StepWaiting } from "./components/step-waiting";
import { StepWebsite } from "./components/step-website";

/**
 * The steps the client walks, in order.
 *
 * `waiting` is in the sequence but is skipped whenever the analysis has already
 * landed — which, given the three questions before it, is the normal case.
 */
const STEP_ORDER: OnboardingStep[] = [
  "website",
  "mobile",
  "telephony",
  "agent",
  "waiting",
  "company",
  "hours",
];

/** Mon–Fri 9–18, the same fallback the backend uses when it learns nothing. */
const DEFAULT_SCHEDULE_RRULES =
  "RRULE:FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR;BYHOUR=9;BYMINUTE=0;DURATION=540";

/** The tools the backend switches on when it learns nothing either. */
const DEFAULT_TOOLS = ["basic_tools", "faq", "messages"];

/**
 * What the analysis would have filled in, for when it could not: the gaps only,
 * so nothing the client answered is touched.
 *
 * Without it a failed analysis left the review with closed hours every day and
 * no tool ticked, and one click on "Terminer" saved exactly that.
 */
const withDefaults = (draft: OnboardingDraft): OnboardingDraft => ({
  ...draft,
  company: {
    default_language: "fr",
    default_country: "FR",
    default_timezone: "Europe/Paris",
    ...draft.company,
  },
  schedule_rrules: draft.schedule_rrules || DEFAULT_SCHEDULE_RRULES,
  tools: draft.tools?.length ? draft.tools : DEFAULT_TOOLS,
});

/**
 * Where a saved wizard resumes.
 *
 * `waiting` is not a place to come back to: it has no answer on it and nothing
 * moves off it but the analysis. Its slot is the review, which is saved in its
 * place (see `goTo`) — and the wait is shown again only while the analysis the
 * review needs is still running.
 */
const resumeStep = (
  saved: string,
  analysis: OnboardingAnalysisStatus,
): OnboardingStep => {
  const known = STEP_ORDER.includes(saved as OnboardingStep)
    ? (saved as OnboardingStep)
    : "website";
  const step = known === "waiting" ? "company" : known;
  return step === "company" && analysis === "running" ? "waiting" : step;
};

/**
 * The onboarding wizard.
 *
 * Two things are worth knowing about how this is wired.
 *
 * **The draft is local, checkpointed at step boundaries.** Edits land in React
 * state immediately and are pushed on navigation, not per keystroke — typing
 * must never wait on a round trip, and a refresh costs at most the screen
 * you're on. The server is still the authority on what has been *applied*:
 * this component never writes to the client, the agent or the connectors.
 * `complete` does all of it in one call.
 *
 * **The analysis runs behind the client.** It is started on step 1 and lands
 * whenever it lands; the three steps after it are chosen precisely because they
 * don't depend on it. When it arrives, its draft is merged *under* the local
 * one — the client's own answers always win over an inference about them.
 */
export function OnboardingPage() {
  const { t } = useTranslation("onboarding");
  const clientId = useOnboardingClientId();
  const navigate = useNavigate();

  const { data: state, isLoading, isError, refetch } = useOnboarding();
  const startAnalysis = useStartAnalysis();
  const saveDraft = useSaveOnboardingDraft();
  const completeOnboarding = useCompleteOnboarding();
  const requestCallback = useRequestCallback();
  const [initialCallback, setInitialCallback] = useState<{
    sessionId: string;
    remaining: number;
  } | null>(null);

  const [step, setStep] = useState<OnboardingStep>("website");
  const [draft, setDraft] = useState<OnboardingDraft>({});
  const [startError, setStartError] = useState<string | null>(null);
  const [result, setResult] = useState<OnboardingResult | null>(null);
  const [hydrated, setHydrated] = useState(false);

  const analysisStatus = state?.onboarding.analysis_status ?? "idle";
  const analysisPending = analysisStatus === "running";
  // The analysis's outcome has been folded into the draft — its inference when
  // it succeeded, the defaults when it failed. Once only: after that the draft
  // is the client's.
  const analysisSettled = useRef(false);

  // Adopt the server's draft once, on first load. After that this component
  // owns it — re-syncing on every poll would overwrite in-progress edits.
  useEffect(() => {
    if (hydrated || !state) return;

    const onboarding = state.onboarding;
    if (onboarding.status === "completed" || onboarding.status === "skipped") {
      navigate(`/${clientId}/home`, { replace: true });
      return;
    }

    // The account's own number is *not* taken as the mobile here. It used to
    // be, marked verified, which skipped the mobile step — but the number
    // typed at sign-up was never proven, and this is what made it the demo
    // line's caller number. `mobile_verified` is the server's to set; the
    // mobile step offers the account's number and proves it (`step-mobile`).
    const saved = onboarding.draft || {};
    // Coming back after a failed analysis: the defaults it never got.
    const failed = onboarding.analysis_status === "failed";
    setDraft(failed ? withDefaults(saved) : saved);
    setStep(resumeStep(onboarding.current_step, onboarding.analysis_status));
    analysisSettled.current = failed || onboarding.analysis_status === "done";
    setHydrated(true);
  }, [state, hydrated, clientId, navigate]);

  // Where the client is and how long each answer took: the funnel the onboarding
  // is judged on. Keyed on the step, so a re-render never counts twice.
  const shownStep = useRef<{ step: OnboardingStep; at: number } | null>(null);
  useEffect(() => {
    if (!hydrated) return;
    const previous = shownStep.current;
    if (previous?.step === step) return;
    trackStepViewed(
      clientId,
      step,
      previous
        ? { step: previous.step, durationMs: Date.now() - previous.at }
        : undefined,
    );
    shownStep.current = { step, at: Date.now() };
  }, [hydrated, step, clientId]);

  useEffect(() => {
    if (isError && !state) trackError(clientId, "load", step);
    // Once per failure, not per step change behind it.
  }, [isError]);

  // The analysis's outcome, once, when it is seen to change from running.
  const lastAnalysisStatus = useRef(analysisStatus);
  useEffect(() => {
    const before = lastAnalysisStatus.current;
    lastAnalysisStatus.current = analysisStatus;
    if (before !== "running") return;
    if (analysisStatus !== "done" && analysisStatus !== "failed") return;
    const startedAt = state?.onboarding.analysis_started_at;
    trackAnalysisOutcome(
      clientId,
      analysisStatus,
      startedAt ? Date.now() - startedAt : null,
      step,
    );
  }, [analysisStatus, state, clientId, step]);

  /** Shallow-merge a step's slice into the draft. */
  const patchDraft = (patch: OnboardingDraft) =>
    setDraft((current) => ({ ...current, ...patch }));

  /**
   * Move to a step and checkpoint the draft.
   *
   * `snapshot` is for callers that change the draft and navigate in the same
   * tick: `draft` is still the pre-update value in their closure, so they pass
   * what they just built instead.
   */
  const goTo = async (
    next: OnboardingStep,
    options?: { snapshot?: OnboardingDraft; phoneChallengeToken?: string },
  ) => {
    setStep(next);
    try {
      await saveDraft.mutateAsync({
        draft: options?.snapshot ?? draft,
        // The wait is saved as the review it stands in front of: a reload must
        // never land on a screen nothing moves off (see `resumeStep`).
        current_step: next === "waiting" ? "company" : next,
        phone_challenge_token: options?.phoneChallengeToken,
      });
    } catch {
      // Losing a checkpoint is not worth interrupting the flow: the draft is
      // still in memory, gets sent again on the next step, and in full on
      // `complete`.
    }
  };

  // The analysis came back while the client was answering. Fold it in under
  // what they have said — their answers are not guesses, ours are — or, when it
  // failed, fill the gaps with the defaults it would have fallen back on.
  //
  // A client who outran it is sitting on the wait: a success takes them on to
  // the review. Nothing else did, and the wait was a dead end — a reload landed
  // on it again. A failure stays there: that screen says what happened and has
  // its own way on.
  useEffect(() => {
    if (!hydrated || analysisSettled.current || !state) return;
    if (analysisStatus !== "done" && analysisStatus !== "failed") return;

    analysisSettled.current = true;
    const settled =
      analysisStatus === "done"
        ? mergeUnder(state.onboarding.draft || {}, draft)
        : withDefaults(draft);
    setDraft(settled);
    if (step === "waiting" && analysisStatus === "done") {
      void goTo("company", { snapshot: settled });
    }
    // `draft` and `step` change on every answer: `analysisSettled` is what
    // keeps this to once.
  }, [analysisStatus, hydrated, state, draft, step]);

  /**
   * The next step, skipping the ones with nothing left to ask.
   *
   * `waiting` goes when the analysis has already landed. `mobile` goes only
   * when the draft's mobile is already verified — which the server alone
   * decides, from a code or a number the account had proven (a client coming
   * back to the wizard). It still shows up as an answered turn in the thread,
   * so the client can see which number we took — and stepping back from the
   * next question reaches it if it is the wrong one.
   */
  const nextStepFrom = (current: OnboardingStep): OnboardingStep => {
    const index = STEP_ORDER.indexOf(current);
    const candidate = STEP_ORDER[Math.min(index + 1, STEP_ORDER.length - 1)];
    if (candidate === "waiting" && !analysisPending) return "company";
    if (candidate === "mobile" && draft.contact?.mobile_verified) {
      return "telephony";
    }
    return candidate;
  };

  const previousStepFrom = (current: OnboardingStep): OnboardingStep => {
    const index = STEP_ORDER.indexOf(current);
    const candidate = STEP_ORDER[Math.max(index - 1, 0)];
    // Never walk *back* into the wait — it is not a screen with an answer on it.
    if (candidate === "waiting") return "agent";
    return candidate;
  };

  const goNext = () => void goTo(nextStepFrom(step));
  const goBack = () => void goTo(previousStepFrom(step));

  const handleStartAnalysis = async (input: {
    url?: string;
    description?: string;
  }) => {
    setStartError(null);
    analysisSettled.current = false;

    // No language, country or timezone of our own here: they are the analysis's
    // to infer, and a local value would win the merge over it (`mergeUnder`) —
    // a Belgian salon stayed French. `withDefaults` fills them if it fails.
    const seeded: OnboardingDraft = {
      ...draft,
      company: {
        ...draft.company,
        ...(input.url ? { website: input.url } : {}),
      },
    };
    setDraft(seeded);

    try {
      await startAnalysis.mutateAsync(input);
      // Saved, not just shown: a reload on the next question used to land back
      // here and start a second analysis.
      void goTo(seeded.contact?.mobile_verified ? "telephony" : "mobile", {
        snapshot: seeded,
      });
    } catch (error) {
      trackError(clientId, "analysis_start", "website");
      setStartError(onboardingErrorMessage(error, t, "errors.analysisFailed"));
    }
  };

  const handleVerifiedMobile = async (
    phone: string,
    phoneChallengeToken?: string,
  ) => {
    const seeded: OnboardingDraft = {
      ...draft,
      contact: { mobile_phone: phone, mobile_verified: false },
    };
    setDraft(seeded);

    // Checkpoint immediately: the OTP token is only valid for two minutes, and
    // it is the server's only proof. Waiting for "Continue" could lose it.
    try {
      const onboarding = await saveDraft.mutateAsync({
        draft: seeded,
        current_step: "mobile",
        phone_challenge_token: phoneChallengeToken,
      });
      // The number and its verified flag come back from the server — it decodes
      // the token itself, so this is the only trustworthy source for both.
      const contact = onboarding.draft?.contact;
      setDraft((current) => ({
        ...current,
        contact: contact ?? current.contact,
      }));

      // A correct code is the answer; making them press Continue afterwards
      // asks them to confirm a confirmation.
      if (contact?.mobile_verified) {
        void goTo("telephony", {
          snapshot: { ...seeded, contact },
        });
      }
    } catch (error) {
      trackError(clientId, "mobile", "mobile");
      toast.error(onboardingErrorMessage(error, t, "errors.mobileFailed"));
    }
  };

  const handleChangeNumber = () =>
    setDraft((current) => ({
      ...current,
      contact: { mobile_phone: "", mobile_verified: false },
    }));

  /**
   * "Terminer plus tard": back to the back-office, with what is on screen kept.
   *
   * The wizard had no way out at all — no skip, no dashboard, no sign-out —
   * so a client who needed to stop had to know to type another address. This
   * leaves the onboarding `in_progress`: it is not skipped, and the back-office
   * offers to resume it (`ResumeOnboardingBanner`).
   */
  const handleLeave = async () => {
    try {
      await saveDraft.mutateAsync({
        draft,
        current_step: step === "waiting" ? "company" : step,
      });
    } catch {
      // Leaving must not depend on the save: the last step boundary is saved.
    }
    navigate(`/${clientId}/home`);
  };

  /** The analysis failed: carry on with the defaults rather than trapping them. */
  const handleContinueAnyway = () => {
    const seeded = withDefaults(draft);
    setDraft(seeded);
    void goTo("company", { snapshot: seeded });
  };

  const handleComplete = async ({ callMe }: { callMe: boolean }) => {
    let completion: OnboardingResult;
    try {
      completion = await completeOnboarding.mutateAsync(draft);
      trackCompleted(clientId, {
        installType: draft.telephony?.install_type,
        hasDemoLine: Boolean(
          completion.demo_number && completion.caller_number,
        ),
        startedAt: state?.onboarding.created_at ?? null,
      });
    } catch (error) {
      trackError(clientId, "complete", "hours");
      toast.error(onboardingErrorMessage(error, t, "errors.completeFailed"));
      return;
    }

    // The setup is applied either way; a call that fails to go out is said,
    // and the next screen still offers it again, and the number to dial.
    if (callMe && completion.demo_number && completion.caller_number) {
      try {
        const placed = await requestCallback.mutateAsync();
        setInitialCallback({
          sessionId: placed.session_id,
          remaining: placed.remaining,
        });
      } catch (error) {
        toast.error(onboardingErrorMessage(error, t, "errors.callbackFailed"));
      }
    }
    setResult(completion);
    setStep("done");
  };

  /**
   * The exchanges already behind the client, replayed as chat history.
   *
   * Built from the draft rather than recorded as they happen: the draft is the
   * only thing that survives a refresh, so deriving the transcript from it is
   * what makes the conversation still be there when they come back.
   */
  const turns = useMemo<OnboardingTurn[]>(() => {
    const answered: OnboardingTurn[] = [];
    const upTo = STEP_ORDER.indexOf(step);

    for (const past of STEP_ORDER.slice(0, upTo < 0 ? 0 : upTo)) {
      const answer = answerFor(
        past,
        draft,
        state?.onboarding.description || "",
        t,
      );
      if (!answer) continue;
      answered.push({
        step: past,
        question: t(`steps.${past}.title`),
        answer,
      });
    }
    return answered;
  }, [step, draft, state?.onboarding.description, t]);

  // A failed load used to leave a blank page, with nothing to click.
  if (isError && !state) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 p-6 text-center">
        <p className="text-muted-foreground">{t("errors.loadFailed")}</p>
        <div className="flex flex-wrap justify-center gap-2">
          <Button type="button" onClick={() => void refetch()}>
            {t("actions.retry")}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(`/${clientId}/home`)}
          >
            {t("actions.toDashboard")}
          </Button>
        </div>
      </div>
    );
  }

  if (isLoading || !hydrated) {
    return <div className="min-h-dvh" />;
  }

  const screen = () => {
    if (step === "done" && result) {
      return (
        <StepDone
          result={result}
          clientId={clientId}
          initialCallback={initialCallback}
        />
      );
    }
    return stepScreen();
  };

  function stepScreen() {
    switch (step) {
      case "mobile":
        return (
          <StepMobile
            draft={draft}
            onVerified={(phone, token) =>
              void handleVerifiedMobile(phone, token)
            }
            onChangeNumber={handleChangeNumber}
            onNext={goNext}
            onBack={goBack}
            isSubmitting={saveDraft.isPending}
            analysisPending={analysisPending}
          />
        );
      case "telephony":
        return (
          <StepTelephony
            draft={draft}
            onChange={patchDraft}
            onNext={goNext}
            onBack={goBack}
            isSubmitting={saveDraft.isPending}
            analysisPending={analysisPending}
          />
        );
      case "agent":
        return (
          <StepAgent
            draft={draft}
            onChange={patchDraft}
            onNext={goNext}
            onBack={goBack}
            isSubmitting={saveDraft.isPending}
            analysisPending={analysisPending}
          />
        );
      case "waiting":
        return (
          <StepWaiting
            source={state?.onboarding.website || ""}
            failed={analysisStatus === "failed"}
            onContinueAnyway={handleContinueAnyway}
          />
        );
      case "company":
        return (
          <StepCompany
            draft={draft}
            analysisFailed={analysisStatus === "failed"}
            onChange={patchDraft}
            onNext={goNext}
            onBack={goBack}
            isSubmitting={saveDraft.isPending}
          />
        );
      case "hours":
        return (
          <StepHours
            draft={draft}
            onChange={patchDraft}
            onComplete={handleComplete}
            onBack={goBack}
            isSubmitting={
              completeOnboarding.isPending || requestCallback.isPending
            }
          />
        );
      default:
        return (
          <StepWebsite
            website={state?.onboarding.website || ""}
            description={state?.onboarding.description || ""}
            onStart={handleStartAnalysis}
            isStarting={startAnalysis.isPending}
            error={startError}
          />
        );
    }
  }

  return (
    <OnboardingChatProvider
      value={{
        turns,
        agentName: draft.agent?.name,
        avatarId: draft.agent?.avatar_id,
        // Nothing left to finish once it is done: the screen has its own way on.
        onLeave: step === "done" ? undefined : () => void handleLeave(),
      }}
    >
      {screen()}
    </OnboardingChatProvider>
  );
}

/**
 * How a completed step reads back in the transcript.
 *
 * Short and in the client's own terms — this is what they said, not a dump of
 * what got stored. Returns null for a step with nothing worth replaying, which
 * simply drops it from the history.
 */
function answerFor(
  step: OnboardingStep,
  draft: OnboardingDraft,
  description: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: (key: string, options?: any) => string,
): string | null {
  switch (step) {
    case "website":
      return draft.company?.website || description || null;
    case "mobile": {
      const phone = draft.contact?.mobile_phone;
      return phone ? formatPhoneNumberIntl(phone) || phone : null;
    }
    case "telephony": {
      const choice = draft.telephony?.install_type;
      return choice ? t(`steps.telephony.options.${choice}.title`) : null;
    }
    case "agent":
      return draft.agent?.name || null;
    case "company":
      return draft.company?.name || null;
    default:
      return null;
  }
}

/**
 * Merge `base` under `overlay`: every value the client set wins, and only the
 * gaps are filled from the inference. Arrays are taken whole from whichever
 * side has them, since a half-merged week of opening hours is nonsense.
 */
function mergeUnder(
  base: OnboardingDraft,
  overlay: OnboardingDraft,
): OnboardingDraft {
  const merged: OnboardingDraft = { ...base, ...overlay };

  for (const key of ["company", "contact", "agent", "telephony"] as const) {
    const baseSection = base[key];
    const overlaySection = overlay[key];
    if (!baseSection && !overlaySection) continue;

    // Drop the overlay's empty strings so a field the client never typed into
    // still gets the inferred value.
    const kept = Object.fromEntries(
      Object.entries(overlaySection || {}).filter(
        ([, value]) => value !== "" && value !== undefined && value !== null,
      ),
    );
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (merged as any)[key] = { ...baseSection, ...kept };
  }

  return merged;
}
